import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Building, AlertTriangle, CheckCircle, XCircle, Flag, 
  History, Merge, Search, RefreshCw, Check, X, Eye, Plus, Trash2, MapPin, Loader2, Navigation
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MUNICIPIOS, AREAS_TRABAJO, TIPOS_INSTITUCION, LOCALIDADES_POR_MUNICIPIO, tieneLocalidadesPredefinidas, getLocalidades } from '../utils/constants';
import RecurseroMapa, { MapaSelector } from '../components/RecurseroMapa';
import DireccionAsistida from '../components/DireccionAsistida';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPOS_REPORTE_LABELS = {
  "telefono_no_funciona": "Teléfono no funciona",
  "direccion_incorrecta": "Dirección incorrecta",
  "cambio_horario": "Cambio de horario",
  "cerrada": "Institución cerrada",
  "nuevo_dato": "Nuevo dato",
  "otro": "Otro"
};

export default function AdminInstituciones() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  const [reportesPendientes, setReportesPendientes] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  
  // Modal de resolver reporte
  const [showResolverModal, setShowResolverModal] = useState(null);
  const [accionReporte, setAccionReporte] = useState('resolver');
  const [comentarioResolucion, setComentarioResolucion] = useState('');
  
  // Modal de cambiar estado
  const [showEstadoModal, setShowEstadoModal] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [motivoEstado, setMotivoEstado] = useState('');
  
  // Modal de fusionar
  const [showFusionarModal, setShowFusionarModal] = useState(false);
  const [instPrincipal, setInstPrincipal] = useState('');
  const [instSecundaria, setInstSecundaria] = useState('');

  // Modal de eliminar institución
  const [showEliminarModal, setShowEliminarModal] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  // Modal de crear institución
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [nuevaInstitucion, setNuevaInstitucion] = useState({
    nombre: '',
    nombre_organismo: '',
    area_desempeno: '',
    tipo_institucion: '',
    areas: [],
    telefono: '',
    email: '',
    web: '',
    horarios: '',
    demandas_atiende: '',
    indicaciones: '',
    responsable: '',
    etiquetas: [],
    // Dirección usando DireccionAsistida
    direccion: {
      municipio: '',
      localidad: '',
      calle: '',
      numero: '',
      lat: null,
      lon: null,
      direccion_texto: '',
      precision: '',
      fuente_geocodificacion: ''
    }
  });
  
  // Etiquetas disponibles
  const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
  
  // Estado para detección de duplicados
  const [posiblesDuplicados, setPosiblesDuplicados] = useState([]);
  const [buscandoDuplicados, setBuscandoDuplicados] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    cargarDatos();
    cargarEtiquetas();
  }, []);

  const cargarEtiquetas = async () => {
    try {
      const response = await axios.get(`${API_URL}/recursero-geo/etiquetas`);
      setEtiquetasDisponibles(response.data);
    } catch (err) {
      console.error('Error cargando etiquetas:', err);
    }
  };

  const toggleEtiqueta = (etiquetaId) => {
    setNuevaInstitucion(prev => ({
      ...prev,
      etiquetas: prev.etiquetas.includes(etiquetaId)
        ? prev.etiquetas.filter(id => id !== etiquetaId)
        : [...prev.etiquetas, etiquetaId]
    }));
  };

  // Detectar duplicados cuando cambian los campos relevantes
  useEffect(() => {
    const detectarDuplicados = async () => {
      if (!nuevaInstitucion.nombre_organismo || !nuevaInstitucion.municipio) {
        setPosiblesDuplicados([]);
        return;
      }
      
      if (nuevaInstitucion.nombre_organismo.length < 3) {
        setPosiblesDuplicados([]);
        return;
      }

      setBuscandoDuplicados(true);
      try {
        const params = new URLSearchParams({
          nombre_organismo: nuevaInstitucion.nombre_organismo,
          municipio: nuevaInstitucion.municipio
        });
        if (nuevaInstitucion.nombre) params.append('nombre', nuevaInstitucion.nombre);
        if (nuevaInstitucion.area_desempeno) params.append('area_desempeno', nuevaInstitucion.area_desempeno);
        
        const response = await axios.post(`${API_URL}/instituciones/detectar-duplicados?${params.toString()}`);
        setPosiblesDuplicados(response.data);
      } catch (err) {
        console.error('Error detectando duplicados:', err);
        setPosiblesDuplicados([]);
      } finally {
        setBuscandoDuplicados(false);
      }
    };

    const timeout = setTimeout(detectarDuplicados, 500);
    return () => clearTimeout(timeout);
  }, [nuevaInstitucion.nombre_organismo, nuevaInstitucion.municipio, nuevaInstitucion.nombre, nuevaInstitucion.area_desempeno]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [statsRes, reportesRes, instRes] = await Promise.all([
        axios.get(`${API_URL}/admin/instituciones/estadisticas`, { headers }),
        axios.get(`${API_URL}/admin/instituciones/reportes-pendientes`, { headers }),
        axios.post(`${API_URL}/instituciones/buscar`, {})
      ]);
      setEstadisticas(statsRes.data);
      setReportesPendientes(reportesRes.data);
      setInstituciones(instRes.data);
    } catch (err) {
      console.error('Error cargando datos:', err);
    } finally {
      setLoading(false);
    }
  };

  const resolverReporte = async (reporteId) => {
    try {
      await axios.put(
        `${API_URL}/admin/instituciones/reportes/${reporteId}/resolver`,
        { accion: accionReporte, comentario_resolucion: comentarioResolucion },
        { headers }
      );
      setShowResolverModal(null);
      setComentarioResolucion('');
      cargarDatos();
    } catch (err) {
      console.error('Error resolviendo reporte:', err);
    }
  };

  const cambiarEstado = async (institucionId) => {
    try {
      await axios.put(
        `${API_URL}/admin/instituciones/${institucionId}/estado`,
        { estado: nuevoEstado, motivo: motivoEstado },
        { headers }
      );
      setShowEstadoModal(null);
      setNuevoEstado('');
      setMotivoEstado('');
      cargarDatos();
    } catch (err) {
      console.error('Error cambiando estado:', err);
    }
  };

  const fusionarInstituciones = async () => {
    if (!instPrincipal || !instSecundaria || instPrincipal === instSecundaria) return;
    
    try {
      await axios.post(
        `${API_URL}/admin/instituciones/fusionar`,
        { 
          institucion_principal_id: instPrincipal, 
          institucion_secundaria_id: instSecundaria 
        },
        { headers }
      );
      setShowFusionarModal(false);
      setInstPrincipal('');
      setInstSecundaria('');
      cargarDatos();
    } catch (err) {
      console.error('Error fusionando:', err);
    }
  };

  const eliminarInstitucion = async () => {
    if (!showEliminarModal) return;
    
    setEliminando(true);
    try {
      await axios.delete(`${API_URL}/instituciones/${showEliminarModal.id}`, { headers });
      setShowEliminarModal(null);
      cargarDatos();
    } catch (err) {
      console.error('Error eliminando:', err);
      alert(err.response?.data?.detail || 'Error al eliminar la institución');
    } finally {
      setEliminando(false);
    }
  };

  const crearInstitucion = async () => {
    try {
      const dir = nuevaInstitucion.direccion;
      
      // Preparar datos con el nuevo formato de dirección
      const datosEnviar = {
        nombre: nuevaInstitucion.nombre,
        nombre_organismo: nuevaInstitucion.nombre_organismo,
        area_desempeno: nuevaInstitucion.area_desempeno,
        tipo_institucion: nuevaInstitucion.tipo_institucion,
        areas: nuevaInstitucion.areas,
        telefono: nuevaInstitucion.telefono,
        email: nuevaInstitucion.email,
        web: nuevaInstitucion.web,
        horarios: nuevaInstitucion.horarios,
        demandas_atiende: nuevaInstitucion.demandas_atiende,
        indicaciones: nuevaInstitucion.indicaciones,
        responsable: nuevaInstitucion.responsable,
        etiquetas: nuevaInstitucion.etiquetas,
        // Datos de ubicación desde DireccionAsistida
        municipio: dir.municipio,
        localidad: dir.localidad,
        domicilio: dir.calle && dir.numero ? `${dir.calle} ${dir.numero}` : '',
        coordenadas: dir.lat && dir.lon ? { lat: dir.lat, lng: dir.lon } : null,
        direccion_texto: dir.direccion_texto,
        precision_geocodificacion: dir.precision,
        fuente_geocodificacion: dir.fuente_geocodificacion
      };
      
      // Agregar location en formato GeoJSON si hay coordenadas
      if (datosEnviar.coordenadas) {
        datosEnviar.location = {
          type: "Point",
          coordinates: [datosEnviar.coordenadas.lng, datosEnviar.coordenadas.lat]
        };
      }
      
      await axios.post(`${API_URL}/instituciones/crear-minima`, datosEnviar, { headers });
      setShowCrearModal(false);
      setNuevaInstitucion({
        nombre: '',
        nombre_organismo: '',
        area_desempeno: '',
        tipo_institucion: '',
        areas: [],
        telefono: '',
        email: '',
        web: '',
        horarios: '',
        demandas_atiende: '',
        indicaciones: '',
        responsable: '',
        etiquetas: [],
        direccion: {
          municipio: '',
          localidad: '',
          calle: '',
          numero: '',
          lat: null,
          lon: null,
          direccion_texto: '',
          precision: '',
          fuente_geocodificacion: ''
        }
      });
      setPosiblesDuplicados([]);
      cargarDatos();
    } catch (err) {
      console.error('Error creando institución:', err);
    }
  };

  const toggleNuevaInstitucionArea = (area) => {
    setNuevaInstitucion(prev => ({
      ...prev,
      areas: prev.areas.includes(area)
        ? prev.areas.filter(a => a !== area)
        : [...prev.areas, area]
    }));
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) + ' ' + d.toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit' });
  };

  const institucionesFiltradas = instituciones.filter(i => 
    !busqueda || i.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (i.nombre_organismo && i.nombre_organismo.toLowerCase().includes(busqueda.toLowerCase())) ||
    (i.area_desempeno && i.area_desempeno.toLowerCase().includes(busqueda.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building className="w-6 h-6" />
          Gestión de Instituciones
        </h1>
        <div className="flex gap-2">
          <Button onClick={() => setShowCrearModal(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Cargar Institución
          </Button>
          <Button onClick={cargarDatos} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-gray-900">{estadisticas.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-green-600">{estadisticas.activas}</p>
              <p className="text-sm text-gray-500">Activas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-yellow-600">{estadisticas.dudosas}</p>
              <p className="text-sm text-gray-500">Dudosas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-red-600">{estadisticas.cerradas}</p>
              <p className="text-sm text-gray-500">Cerradas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-blue-600">{estadisticas.geolocalizadas || 0}</p>
              <p className="text-sm text-gray-500">Con Geo</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-orange-600">{estadisticas.reportes_pendientes}</p>
              <p className="text-sm text-gray-500">Reportes</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reportes Pendientes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flag className="w-5 h-5 text-orange-500" />
            Reportes Pendientes ({reportesPendientes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reportesPendientes.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay reportes pendientes 🎉</p>
          ) : (
            <div className="space-y-3">
              {reportesPendientes.map(r => (
                <div key={r.id} className="p-4 border rounded-lg bg-orange-50 border-orange-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="bg-orange-100">
                          {TIPOS_REPORTE_LABELS[r.tipo_reporte] || r.tipo_reporte}
                        </Badge>
                        <span className="text-sm text-gray-500">{formatFecha(r.fecha)}</span>
                      </div>
                      <p className="font-medium text-gray-900">{r.institucion_nombre}</p>
                      <p className="text-sm text-gray-500">{r.institucion_municipio}</p>
                      {r.comentario && (
                        <p className="text-sm text-gray-700 mt-2 italic">"{r.comentario}"</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Reportado por: {r.autor_nombre}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/instituciones/${r.institucion_id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowResolverModal(r)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Resolver
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Instituciones */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Instituciones
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFusionarModal(true)}
            >
              <Merge className="w-4 h-4 mr-2" />
              Fusionar duplicados
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar institución..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {institucionesFiltradas.map(inst => {
              const estadoConfig = {
                activa: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
                dudosa: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
                cerrada: { color: 'bg-red-100 text-red-700', icon: XCircle }
              };
              const config = estadoConfig[inst.estado] || estadoConfig.activa;
              const Icon = config.icon;
              
              return (
                <div key={inst.id} className="p-3 border rounded-lg hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={config.color}>
                      <Icon className="w-3 h-3 mr-1" />
                      {inst.estado}
                    </Badge>
                    {/* Badge de geolocalización */}
                    {inst.coordenadas?.lat && inst.coordenadas?.lng ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <MapPin className="w-3 h-3 mr-1" />
                        Geo
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                        <MapPin className="w-3 h-3 mr-1" />
                        Sin geo
                      </Badge>
                    )}
                    <div>
                      <p className="font-medium">{inst.nombre}</p>
                      <p className="text-sm text-gray-500">{inst.municipio}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/instituciones/${inst.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowEstadoModal(inst);
                        setNuevoEstado(inst.estado);
                      }}
                    >
                      Cambiar estado
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setShowEliminarModal(inst)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal Resolver Reporte */}
      {showResolverModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Resolver Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                <strong>Institución:</strong> {showResolverModal.institucion_nombre}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Tipo:</strong> {TIPOS_REPORTE_LABELS[showResolverModal.tipo_reporte]}
              </p>
              {showResolverModal.comentario && (
                <p className="text-sm text-gray-600">
                  <strong>Comentario:</strong> "{showResolverModal.comentario}"
                </p>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Acción</label>
                <Select value={accionReporte} onValueChange={setAccionReporte}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resolver">✓ Resolver (información corregida)</SelectItem>
                    <SelectItem value="rechazar">✗ Rechazar (reporte inválido)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Comentario de resolución</label>
                <Textarea
                  value={comentarioResolucion}
                  onChange={(e) => setComentarioResolucion(e.target.value)}
                  placeholder="Opcional: explicar qué se hizo"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowResolverModal(null)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={() => resolverReporte(showResolverModal.id)} className="flex-1">
                  Confirmar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Cambiar Estado */}
      {showEstadoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Cambiar Estado de Institución</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                <strong>Institución:</strong> {showEstadoModal.nombre}
              </p>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Nuevo estado</label>
                <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activa">✓ Activa</SelectItem>
                    <SelectItem value="dudosa">⚠ Información dudosa</SelectItem>
                    <SelectItem value="cerrada">✗ Cerrada / Mudada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Motivo</label>
                <Textarea
                  value={motivoEstado}
                  onChange={(e) => setMotivoEstado(e.target.value)}
                  placeholder="Opcional: explicar el motivo del cambio"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowEstadoModal(null)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={() => cambiarEstado(showEstadoModal.id)} className="flex-1">
                  Cambiar estado
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Fusionar */}
      {showFusionarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Merge className="w-5 h-5" />
                Fusionar Instituciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                La institución secundaria se eliminará y sus datos (trabajos vinculados, reportes, comentarios) se pasarán a la principal.
              </p>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Institución Principal (se mantiene)</label>
                <Select value={instPrincipal} onValueChange={setInstPrincipal}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {instituciones.map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.nombre} ({i.municipio})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Institución Secundaria (se elimina)</label>
                <Select value={instSecundaria} onValueChange={setInstSecundaria}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {instituciones.filter(i => i.id !== instPrincipal).map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.nombre} ({i.municipio})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {instPrincipal && instSecundaria && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    ⚠️ <strong>Atención:</strong> "{instituciones.find(i => i.id === instSecundaria)?.nombre}" será eliminada permanentemente.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowFusionarModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button 
                  onClick={fusionarInstituciones} 
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={!instPrincipal || !instSecundaria || instPrincipal === instSecundaria}
                >
                  Fusionar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Crear Institución */}
      {showCrearModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Cargar Nueva Institución
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Organismo <span className="text-red-500">*</span></Label>
                <Input
                  value={nuevaInstitucion.nombre_organismo}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, nombre_organismo: e.target.value }))}
                  placeholder="Ej: Ministerio de Desarrollo, HIGA Pte. Perón, Escuela N°5"
                />
                <p className="text-xs text-gray-500">El organismo o institución principal</p>
              </div>

              <div className="space-y-2">
                <Label>Área de Desempeño</Label>
                <Input
                  value={nuevaInstitucion.area_desempeno}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, area_desempeno: e.target.value }))}
                  placeholder="Ej: Dirección de Asistencia Crítica, Servicio de Traumatología, Equipo de Orientación Escolar"
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo de Institución <span className="text-red-500">*</span></Label>
                <Select 
                  value={nuevaInstitucion.tipo_institucion} 
                  onValueChange={(v) => setNuevaInstitucion(prev => ({ ...prev, tipo_institucion: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccioná" /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_INSTITUCION.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Sugerencias de duplicados */}
              {(posiblesDuplicados.length > 0 || buscandoDuplicados) && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {buscandoDuplicados ? 'Buscando instituciones similares...' : '¿Ya existe esta institución?'}
                  </p>
                  {posiblesDuplicados.length > 0 && (
                    <div className="space-y-2">
                      {posiblesDuplicados.map(dup => (
                        <div 
                          key={dup.id}
                          className="p-2 bg-white rounded border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition-colors"
                          onClick={() => navigate(`/instituciones/${dup.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{dup.nombre}</p>
                              {dup.nombre_organismo && <p className="text-xs text-gray-500">{dup.nombre_organismo}</p>}
                              <p className="text-xs text-gray-500">{dup.localidad ? `${dup.localidad}, ` : ''}{dup.municipio}</p>
                            </div>
                            <Badge className="bg-yellow-100 text-yellow-700">
                              {dup.similitud}% similar
                            </Badge>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-yellow-700 mt-2">
                        Hacé click para ver la institución existente, o continuá si es nueva.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Ubicación usando DireccionAsistida */}
              <div className="space-y-2 border rounded-lg p-4 bg-gray-50">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Ubicación de la Institución
                </Label>
                <DireccionAsistida
                  value={nuevaInstitucion.direccion}
                  onChange={(dir) => setNuevaInstitucion(prev => ({ ...prev, direccion: dir }))}
                  showMap={true}
                  mapHeight="200px"
                />
              </div>

              <div className="space-y-2">
                <Label>Áreas de intervención</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 p-3 bg-gray-50 rounded-lg border">
                  {AREAS_TRABAJO.map(area => (
                    <div key={area} className="flex items-center space-x-2">
                      <Checkbox
                        id={`admin-area-${area}`}
                        checked={nuevaInstitucion.areas.includes(area)}
                        onCheckedChange={() => toggleNuevaInstitucionArea(area)}
                      />
                      <label htmlFor={`admin-area-${area}`} className="text-sm cursor-pointer leading-tight">
                        {area}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono {!nuevaInstitucion.email && <span className="text-red-500">*</span>}</Label>
                  <Input
                    value={nuevaInstitucion.telefono}
                    onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, telefono: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email {!nuevaInstitucion.telefono && <span className="text-red-500">*</span>}</Label>
                  <Input
                    type="email"
                    value={nuevaInstitucion.email}
                    onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">Al menos uno (teléfono o email) es obligatorio</p>

              <div className="space-y-2">
                <Label>Sitio web</Label>
                <Input
                  value={nuevaInstitucion.web}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, web: e.target.value }))}
                  placeholder="www.institucion.gob.ar"
                />
              </div>

              {/* Etiquetas / Características */}
              {etiquetasDisponibles.length > 0 && (
                <div className="space-y-2">
                  <Label>Características / Etiquetas</Label>
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50">
                    {etiquetasDisponibles.map(etq => (
                      <button
                        key={etq.id}
                        type="button"
                        onClick={() => toggleEtiqueta(etq.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          nuevaInstitucion.etiquetas.includes(etq.id)
                            ? 'text-white shadow-md'
                            : 'bg-white text-gray-700 border hover:border-gray-400'
                        }`}
                        style={nuevaInstitucion.etiquetas.includes(etq.id) ? { backgroundColor: etq.color } : {}}
                      >
                        {etq.nombre}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Seleccioná las características que apliquen (para filtros de búsqueda)</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Horarios de atención</Label>
                <Input
                  value={nuevaInstitucion.horarios}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, horarios: e.target.value }))}
                  placeholder="Ej: Lunes a Viernes 9-15hs"
                />
              </div>

              <div className="space-y-2">
                <Label>Demandas que atiende</Label>
                <Textarea
                  value={nuevaInstitucion.demandas_atiende}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, demandas_atiende: e.target.value }))}
                  placeholder="Describir qué tipo de demandas o problemáticas atiende la institución"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Indicaciones / Criterios institucionales</Label>
                <Textarea
                  value={nuevaInstitucion.indicaciones}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, indicaciones: e.target.value }))}
                  placeholder="Información útil para colegas (requisitos, documentación, tips para derivar)"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Responsable / Referente del área</Label>
                <Input
                  value={nuevaInstitucion.responsable}
                  onChange={(e) => setNuevaInstitucion(prev => ({ ...prev, responsable: e.target.value }))}
                  placeholder="Ej: Lic. María García"
                />
                <p className="text-xs text-gray-500">Nombre del responsable o referente institucional (opcional)</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCrearModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button 
                  onClick={crearInstitucion}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={
                    !nuevaInstitucion.nombre_organismo ||
                    !nuevaInstitucion.tipo_institucion || 
                    !nuevaInstitucion.direccion?.municipio ||
                    (tieneLocalidadesPredefinidas(nuevaInstitucion.direccion?.municipio) && !nuevaInstitucion.direccion?.localidad) ||
                    (!nuevaInstitucion.telefono && !nuevaInstitucion.email)
                  }
                >
                  Crear Institución
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Eliminar Institución */}
      {showEliminarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Eliminar Institución
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  ⚠️ <strong>¡Atención!</strong> Esta acción eliminará permanentemente:
                </p>
                <ul className="text-sm text-red-700 mt-2 ml-4 list-disc">
                  <li>La institución y todos sus datos</li>
                  <li>Todos los comentarios asociados</li>
                  <li>El historial de cambios</li>
                  <li>Los reportes pendientes</li>
                  <li>Se desvincularán los profesionales asociados</li>
                </ul>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{showEliminarModal.nombre}</p>
                <p className="text-sm text-gray-500">{showEliminarModal.municipio}</p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowEliminarModal(null)} 
                  className="flex-1"
                  disabled={eliminando}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={eliminarInstitucion}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={eliminando}
                >
                  {eliminando ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
